import Location from "./collisionWarning/Location";
import moment from "moment";
import * as CollisionLogicUtil from "./collisionWarning/CollisionLogicUtil";
import * as LightWeightGlobalLogic from "./collisionWarning/LightWeightGlobalLogic";
import {
  VESSEL_CATEGORY,
  NAVIGATION_STATUS,
  EQUIPMENT_VESSEL_STATUS,
} from "@/config/const";
import * as turf from "@turf/turf";

export default class VesselMovement {
  constructor(timestamp, location, direction, heading, velocity, isExpiring) {
    this._timestamp = timestamp ? moment(timestamp).utc() : moment().utc();
    this._location = new Location(location.latitude, location.longitude);
    this._direction = direction;
    this._heading = heading;
    this._velocity = velocity;
    this._isExpiring = isExpiring;
  }

  isActive() {
    return this._velocity > 1.0;
  }

  get timestamp() {
    return this._timestamp;
  }

  get location() {
    return this._location;
  }

  get direction() {
    return this._direction;
  }

  get heading() {
    return this._heading;
  }

  get velocity() {
    return this._velocity;
  }

  get isExpiring() {
    return this._isExpiring;
  }
}

export const getCenterCoordinationFromAIS = (info, position) => {
  let latitude = position.l.latitude || position.l._lat;
  let longitude = position.l.longitude || position.l._long;
  if (!info || !info.w || !info.l) {
    return new Location(latitude, longitude);
  }

  const width = info.w;
  const length = info.l;

  const starboardLen = info.d_sb ? info.d_sb : width / 2.0;
  const portLen = info.d_p ? info.d_p : width / 2.0;
  const bowLen = info.d_b ? info.d_b : length / 2.0;
  const sternLen = info.d_s ? info.d_s : length / 2.0;

  const moveForStarboard = starboardLen - (starboardLen + portLen) / 2.0;
  const starboardDirection =
    position.h + 90.0 - 360.0 * Math.ceil((position.h + 90.0) / 360.0);
  const middle = LightWeightGlobalLogic.getArrivalPoint(
    new Location(latitude, longitude),
    starboardDirection,
    moveForStarboard,
  );

  const moveForStern = sternLen - (sternLen + bowLen) / 2.0;
  const sternDirection =
    position.h + 180.0 - 360.0 * Math.ceil((position.h + 180.0) / 360.0);

  return LightWeightGlobalLogic.getArrivalPoint(
    middle,
    sternDirection,
    moveForStern,
  );
};

export const isActive = (velocity) => {
  return velocity > 1.0;
};

export const getSymbolIconName = (vesselInfo, movement, isPrefix) => {
  const target = movement.t ? movement.t : moment().utc();
  const seven = moment().utc().subtract(7, "minutes");
  let iconName = "vessel";
  let category = vesselInfo != null ? vesselInfo.c : VESSEL_CATEGORY.OTHER;
  switch (category) {
    case VESSEL_CATEGORY.TANKER: // Tanker
      iconName += `_tanker${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.CARGO: // Cargo
      iconName += `_cargo${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.TUGBOAT: // Tugboat
      iconName += `_tug${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.CRUISE: // Cruise
      iconName += `_passenger${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.FISHING: // Fishing
      iconName += `_fishing${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.PLEASURE: // Pleasure
      iconName += `_app${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.JET_SKI: // JetSki
      iconName += `_bike${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.OTHER: // Other
      iconName += `_other${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.WORK: // Work
      iconName += `_work${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.SAILBOAT: // Sailboat
      iconName += `_sailboat${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    case VESSEL_CATEGORY.BARGE: // Barge
      iconName += `_barge${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
    default:
      iconName += `_other${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
      }`;
      break;
  }

  if (isPrefix) {
    return "icon_" + iconName;
  }

  return (iconName += "_icon");
};

export const getSymbolIconNameLightWeight = (category, velocity, diff) => {
  let iconName = "v";
  switch (category) {
    case VESSEL_CATEGORY.TANKER: // Tanker
      iconName += `_tan${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.CARGO: // Cargo
      iconName += `_car${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.TUGBOAT: // Tugboat
      iconName += `_tug${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.CRUISE: // Cruise
      iconName += `_pas${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.FISHING: // Fishing
      iconName += `_fis${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.PLEASURE: // App
      iconName += `_ple${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.JET_SKI: // JetSki
      iconName += `_bik${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.OTHER: // Other
      iconName += `_oth${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.WORK: // Work
      iconName += `_wor${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.SAILBOAT: // Sailboat
      iconName += `_sai${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    case VESSEL_CATEGORY.BARGE: // Barge
      iconName += `_bar${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
    default:
      iconName += `_oth${
        isActive(velocity) ? "-a" : diff >= 60 * 7 ? "-d" : ""
      }`;
      break;
  }

  return iconName;
};

export const getSimpleOpacity = (vesselCategory, diff) => {
  const category =
    vesselCategory != null ? vesselCategory : VESSEL_CATEGORY.OTHER;
  switch (category) {
    default:
      return 1.0;
    case VESSEL_CATEGORY.TANKER: // Tanker
    case VESSEL_CATEGORY.CARGO: // Cargo
    case VESSEL_CATEGORY.TUGBOAT: // Tugboat
    case VESSEL_CATEGORY.CRUISE: // Cruise
    case VESSEL_CATEGORY.FISHING: // Fishing
    case VESSEL_CATEGORY.OTHER: // Other
    case VESSEL_CATEGORY.WORK: // Work
    case VESSEL_CATEGORY.SAILBOAT: // Sailboat
    case VESSEL_CATEGORY.BARGE: // Barge
      return diff <= 60 * 7 ? 1.0 : 0.5;
    case VESSEL_CATEGORY.PLEASURE: // Pleasure
    case VESSEL_CATEGORY.JET_SKI: // JetSki
      return diff <= 50 ? 1.0 : 0.5;
  }
};

export const getVectorOpacity = (diff) => {
  return diff <= 60 * 7 ? 0.7 : 0;
};

export const getVesselMovement = (vessel, timestamp) => {
  const positions = vessel.positions.sortBy(
    (value) => value,
    (valueA, valueB) => {
      return valueB.timestamp.isAfter(valueA.timestamp) ? 1 : -1;
    },
  );
  const justBeforeIndex = positions.findIndex((value) =>
    value.timestamp.isSameOrBefore(timestamp),
  );
  const justAfter = positions.get(justBeforeIndex - 1);
  const justBefore = positions.get(justBeforeIndex);
  const nextBefore = positions.get(justBeforeIndex + 1);

  const location = getLocation(
    timestamp,
    justAfter,
    justBefore,
    vessel.information.category.value,
  );
  if (
    justBefore &&
    justBefore.direction &&
    justBefore.velocity &&
    justBefore.heading
  ) {
    return new VesselMovement(
      timestamp,
      location,
      justBefore.direction,
      justBefore.heading,
      justBefore.velocity > 100 ? 0 : justBefore.velocity,
    );
  }

  if (justAfter) {
    const { direction, velocity } = getVector(justAfter, justBefore);
    if (!justBefore.direction) {
      justBefore.direction = direction;
    }
    if (!justBefore.velocity) {
      justBefore.velocity = velocity;
    }
    if (!justBefore.heading) {
      justBefore.heading = direction;
    }

    return new VesselMovement(
      timestamp,
      location,
      justBefore.direction,
      justBefore.heading,
      justBefore.velocity > 100 ? 0 : justBefore.velocity,
    );
  }

  if (
    nextBefore &&
    nextBefore.direction &&
    nextBefore.velocity &&
    nextBefore.heading
  ) {
    return new VesselMovement(
      timestamp,
      location,
      nextBefore.direction,
      nextBefore.heading,
      nextBefore.velocity > 100 ? 0 : nextBefore.velocity,
    );
  }

  if (nextBefore) {
    const { direction, velocity } = getVector(justBefore, nextBefore);
    if (!nextBefore.direction) {
      nextBefore.direction = direction;
    }
    if (!nextBefore.velocity) {
      nextBefore.velocity = velocity;
    }
    if (!nextBefore.heading) {
      nextBefore.heading = direction;
    }

    return new VesselMovement(
      timestamp,
      location,
      nextBefore.direction,
      nextBefore.heading,
      nextBefore.velocity > 100 ? 0 : nextBefore.velocity,
    );
  }

  const direction = justBefore.direction ? justBefore.direction : 0;
  const heading = justBefore.heading ? justBefore.heading : direction;
  return new VesselMovement(timestamp, location, direction, heading, 0);
};

const getVector = (after, before) => {
  if (
    after.location.latitude === before.location.latitude &&
    after.location.longitude === before.location.longitude
  ) {
    return {
      direction: 0,
      velocity: 0,
    };
  }
  const move = LightWeightGlobalLogic.getMovement(
    new Location(before.location.latitude, before.location.longitude),
    new Location(after.location.latitude, after.location.longitude),
  );
  let dir = 0;
  if (isFinite(move.direction)) {
    dir = move.direction;
  }
  const timeDiff = Math.max(after.timestamp.diff(before.timestamp), 0.0001);
  const vel =
    (move.distance * 60.0 * 60.0) / timeDiff / CollisionLogicUtil.MILE;
  return {
    direction: dir,
    velocity: vel,
  };
};

export const getVectorWithLightWeight = (after, before) => {
  if (
    after.l.latitude === before.l.latitude &&
    after.l.longitude === before.l.longitude
  ) {
    return {
      direction: 0,
      velocity: 0,
    };
  }
  const move = LightWeightGlobalLogic.getMovement(
    new Location(before.l.latitude, before.l.longitude),
    new Location(after.l.latitude, after.l.longitude),
  );
  let dir = 0;
  if (isFinite(move.direction)) {
    dir = move.direction;
  }
  const timeDiff = Math.max(after.t - before.t, 0.0001);
  const vel =
    (move.distance * 60.0 * 60.0) / timeDiff / CollisionLogicUtil.MILE;
  return {
    direction: dir,
    velocity: vel,
  };
};

const getLocation = (timestamp, justAfter, justBefore, vesselCategoryId) => {
  const timeBefore = timestamp.diff(justBefore.timestamp);
  if (justAfter) {
    const timeAfter = justAfter.timestamp.diff(timestamp);
    return new Location(
      (timeAfter * justBefore.location.latitude +
        timeBefore * justAfter.location.latitude) /
        (timeAfter + timeBefore),
      (timeAfter * justBefore.location.longitude +
        timeBefore * justAfter.location.longitude) /
        (timeAfter + timeBefore),
    );
  }

  const getExpirationTime = () => {
    switch (vesselCategoryId) {
      default:
        return 7.0 * 60.0;
      case VESSEL_CATEGORY.PLEASURE:
      case VESSEL_CATEGORY.JET_SKI:
        return 60.0;
    }
  };

  if (
    timestamp.isSameOrAfter(
      justBefore.timestamp.add(getExpirationTime(), "seconds"),
    )
  ) {
    return new Location(
      justBefore.location.latitude,
      justBefore.location.longitude,
    );
  }

  return null;
};

export const getStatusLamp = (status) => {
  let statusLamp = {
    color: null,
    blink: false,
  };
  switch (status) {
    case NAVIGATION_STATUS.UNDER_WAY_USING_ENGINE:
    case NAVIGATION_STATUS.UNDER_WAY_SAILING:
      statusLamp.color = "#46B5F4";
      break;
    case NAVIGATION_STATUS.AT_ANCHOR:
    case NAVIGATION_STATUS.MOORED:
    case NAVIGATION_STATUS.RESERVED13:
    case NAVIGATION_STATUS.NOT_AVAILABLE:
    case NAVIGATION_STATUS.NOT_IN_OPARATION:
      statusLamp.color = "#A9ADAF";
      break;
    case NAVIGATION_STATUS.NOT_UNDER_COMMAND:
    case NAVIGATION_STATUS.AGROUND:
    case NAVIGATION_STATUS.SART:
      statusLamp.color = "#F45146";
      break;
    case NAVIGATION_STATUS.RESTRICTED_MANEUVERABILITY:
    case NAVIGATION_STATUS.CONSTRAINED_BY_HER_DRAUGHT:
    case NAVIGATION_STATUS.ENGAGED_IN_FISHING:
    case NAVIGATION_STATUS.RESERVED9:
    case NAVIGATION_STATUS.RESERVED10:
    case NAVIGATION_STATUS.TOWING:
    case NAVIGATION_STATUS.PUSHING:
      statusLamp.color = "#F4D946";
      break;
    default:
      statusLamp.color = "#A9ADAF";
  }

  switch (status) {
    case NAVIGATION_STATUS.AGROUND:
    case NAVIGATION_STATUS.RESERVED10:
    case NAVIGATION_STATUS.TOWING:
    case NAVIGATION_STATUS.PUSHING:
      statusLamp.blink = true;
      break;
  }

  return statusLamp;
};

// 作業船ステータスカラー取得
export const getEquipmentVesselStatusColor = (status) => {
  let color = null;
  switch (status) {
    case EQUIPMENT_VESSEL_STATUS.UNDER_WAY_USING_ENGINE:
      color = "#D1B803";
      break;
    case EQUIPMENT_VESSEL_STATUS.UNDER_WAY_INPUT:
      color = "#F15156";
      break;
    case EQUIPMENT_VESSEL_STATUS.COMPLETE_INPUT:
      color = "#2B93CE";
      break;
    default:
      color = "#A9ADAF";
  }
  return color;
};

// 作業船投入完了判定
export const isEquipmentVesselCompleteInput = (status) => {
  return status === EQUIPMENT_VESSEL_STATUS.COMPLETE_INPUT;
};

/**
 * 到着予定時間のエラーコード
 */
const PREDICT_ERROR_CODE = {
  ENABLE_CALC: {
    /** 船の速度が不明または停止 */
    STOPPED: 1,
    /** 船の平均速度が不明または0 */
    AVERAGE_SPEED: 2,
    /** 該当航路の情報が不明 */
    ROUTE: 3,
    /** 該当航路の情報が1点のポイントのみで構成されている */
    ONE_POINT: 4,
  },
  DISABLE_CALC: {
    /** 船の平均速度が不明または0 */
    AVERAGE_SPEED: 5,
    /** 該当航路の情報が不明 */
    ROUTE: 6,
    /** 該当航路の情報が1点のポイントのみで構成されている */
    ONE_POINT: 7,
    /** 出発時間が不正 */
    DEPARTURE: 8,
  },
};

/**
 * 到着予定時間が計算できない時のエラーメッセージを取得する
 */
function getErrorMessage(n) {
  return `取得できません（${n}）`;
}
/**
 * 航路の到着予定時間を取得する
 * [INFO]
 * 船舶位置：Ps
 * 定点    ：P0, P1, P2,..., Pn
 * 航路区間：L1, L2,..., Ln
 * 船速：Vs
 * 平均船速：Va
 * 【船舶から一番近い定点の判定ロジック】
 * (1) 内積が負の航路が１つ          ：今いる航路の終点～目的地
 * (2) 内積が負の航路が存在しない     ：船舶に一番近い定点～目的地
 * (3) 内積が負の航路が複数          ：最も目的地に近い航路の終点～目的地
 * 【到着予定時間の計算ロジック】
 * ・所要時間 = （船舶位置～一番近い定点の距離）/実際の船速 + 残り航路区間の距離 / 平均船速
 * ・到着予定時間 = 現在時刻 + 所要時間
 * 【到着予定時間の計算を行わない場合】
 * ・航路が存在しない・・・・・・・・・・・・・・「---」を返す
 * ・船速が存在しない or 0（停止している）・・・「---」を返す
 * ・船舶の位置情報が存在しない・・・・・・・・・「---」を返す
 * ・船舶と目的地の距離が100m未満であることを二回連続で検知・・前回取得した到着予定時間を使用する
 * 【例】
 * Psから一番近い点がP1の場合
 * P0 L1 P1 L2 P2 L3 P3 L4 P4
 * ○-----○-----○-----○-----○
 *        \◎ Ps
 *        => P1が一番近い・・・P1~Ps/Vs+(L2+L3+L4)/Va
 * @return { time: "yyyy/mm/dd hh:mm", isFixed: boolean }
 *           time: 表示する時刻、isFixed: true:目的地から100m以内
 */
export const getPredictArrivalTime = (
  routePoints,
  speed,
  vesselPoint,
  averageSpeed,
  departureDatetime,
  isDebug = false,
) => {
  let defaultArrivalTime = {
    time: "---",
  };
  /** 到着予定時間を計算しない条件 */
  // 船舶の位置情報が存在しない
  if (!vesselPoint || !vesselPoint.length) {
    /** 所定時間ルート */
    defaultArrivalTime.time = getPredeterminedTime(
      routePoints,
      departureDatetime,
      averageSpeed,
    );
    return defaultArrivalTime;
  }
  // 航路が存在しない
  if (!routePoints || !routePoints.length) {
    defaultArrivalTime.time = getErrorMessage(
      PREDICT_ERROR_CODE.ENABLE_CALC.ROUTE,
    );
    return defaultArrivalTime;
  }
  // 航路が1点のみ
  if (routePoints.length === 1) {
    defaultArrivalTime.time = getErrorMessage(
      PREDICT_ERROR_CODE.ENABLE_CALC.ONE_POINT,
    );
    return defaultArrivalTime;
  }

  // 距離を求める
  // isArrivedの判定は目的地との距離を直接比較する
  // 船舶の位置座標を取得
  const vesselGeoPoint = turf.point([vesselPoint[1], vesselPoint[0]]);
  const isArrived = isNearDestination(
    routePoints[routePoints.length - 1],
    vesselGeoPoint,
  );
  if (isArrived) {
    defaultArrivalTime.time = "到着済み";
    return defaultArrivalTime;
  }

  // 船速が存在しない or 停止（0.2未満）
  if (!speed || speed < 0.2) {
    defaultArrivalTime.time = getErrorMessage(
      PREDICT_ERROR_CODE.ENABLE_CALC.STOPPED,
    );
    return defaultArrivalTime;
  }
  // 平均船速が不明 or 0
  if (!averageSpeed) {
    defaultArrivalTime.time = getErrorMessage(
      PREDICT_ERROR_CODE.ENABLE_CALC.AVERAGE_SPEED,
    );
    return defaultArrivalTime;
  }

  /** 計算時間ルート */
  // 最短距離の定点のインデックスを確定する
  const nearestPointIndex = getNearestPointIndex(
    vesselPoint,
    routePoints,
    vesselGeoPoint,
  );

  // 船舶と船舶に一番近い定点の距離を求める
  const geoPoint = turf.point([
    routePoints[nearestPointIndex][1],
    routePoints[nearestPointIndex][0],
  ]);
  let shortestDistance = turf.distance(vesselGeoPoint, geoPoint, {
    units: "nauticalmiles",
  });

  // 目的地に一番近いか
  const isLastPoint = nearestPointIndex + 1 === routePoints.length;

  // [TODO] デバッグが終わったら削除する
  if (isDebug) {
    console.log(`航路：：${nearestPointIndex} 番目`);
  }

  // 船舶と最も近い定点までの距離と所要時間(h)
  // [INFO] 船舶停止時は無視する（異常系考慮）
  const vesselToPointTime = speed ? shortestDistance / speed : 0;
  // 航路の合計距離(海里)と所要時間（予測）
  const predictTime = !isLastPoint
    ? getDistance(routePoints, nearestPointIndex) / averageSpeed
    : 0;

  const totalTime = vesselToPointTime + predictTime;
  // 到着時間を求める
  const currentTime = moment(new Date());
  // 到着予定時刻（出発時間 + 航路時間）
  const arrivalTime = moment(currentTime)
    .add(totalTime, "h")
    .format("YYYY/MM/DD HH:mm");
  return {
    time: arrivalTime + " 計算時間",
  };
};

/**
 * 所定時間を計算する
 */
function getPredeterminedTime(routePoints, departureDatetime, averageSpeed) {
  // 航路が存在しない
  if (!routePoints || !routePoints.length) {
    return getErrorMessage(PREDICT_ERROR_CODE.DISABLE_CALC.ROUTE);
  }
  // 航路が1点のみ
  if (routePoints.length === 1) {
    return getErrorMessage(PREDICT_ERROR_CODE.DISABLE_CALC.ONE_POINT);
  }
  // 平均船速が不明 or 0
  if (!averageSpeed) {
    return getErrorMessage(PREDICT_ERROR_CODE.DISABLE_CALC.AVERAGE_SPEED);
  }
  // 出発時刻
  if (departureDatetime === "---") {
    return getErrorMessage(PREDICT_ERROR_CODE.DISABLE_CALC.DEPARTURE);
  }
  // 到着時間を求める
  const departureDatetimeMoment = moment(departureDatetime);
  const predeterminedTime = getDistance(routePoints, 0) / averageSpeed;
  // 到着予定時刻（出発時間 + 航路時間）
  const arrivalTime = moment(departureDatetimeMoment)
    .add(predeterminedTime, "h")
    .format("YYYY/MM/DD HH:mm");
  return arrivalTime + " 所定時間";
}

/**
 * 船舶と目的地の距離が100m以内であるか確認する
 */
function isNearDestination(destination, vesselGeoPoint) {
  // 目的地のGeoPointを取得
  const destinationGeoPoint = turf.point([destination[1], destination[0]]);
  const distance = turf.distance(vesselGeoPoint, destinationGeoPoint, {
    units: "kilometers",
  });
  return distance * 1000 < 100;
}

/**
 * 最短定点の候補を抽出する
 * @param {*} routePoints 航路の座標
 * @param {*} vesselPoint 船舶の座標
 * @returns 最短定点の候補（indexの配列）
 */
function getOnRoutePointIndexes(routePoints, vesselPoint) {
  // 航路の座標をメルカトルに変換
  const vesselMercatorPoint = getMercatorAtlasPosition(vesselPoint);
  const routeMercatorPoints = routePoints.map((point) => {
    return getMercatorAtlasPosition(point);
  });
  // 内積を演算し、内積が負の定点のインデックスを返却
  return routeMercatorPoints.reduce((acc, _val, index, self) => {
    if (index === 0) return [];
    // 航路の始点（xy平面の原点を基準にするため平行移動）
    let startPoint = {
      x: self[index - 1].x - vesselMercatorPoint.x,
      y: self[index - 1].y - vesselMercatorPoint.y,
    };
    // 航路の終点（xy平面の原点を基準にするため平行移動）
    let endPoint = {
      x: self[index].x - vesselMercatorPoint.x,
      y: self[index].y - vesselMercatorPoint.y,
    };
    // 内積を演算する
    const innerProduct = startPoint.x * endPoint.x + startPoint.y * endPoint.y;
    // 内積が負の航路の候補を配列に格納
    if (innerProduct < 0) {
      acc.push(index);
    }
    return acc;
  }, []);
}

/**
 * 船舶に一番近い定点を取得する
 * @param {*} onRoutePointIndexes
 * @param {*} routePoints
 * @param {*} vesselGeoPoint
 * @returns
 */
function getNearestPointIndex(vesselPoint, routePoints, vesselGeoPoint) {
  // 最短定点の候補を抽出する
  const onRoutePointIndexes = getOnRoutePointIndexes(routePoints, vesselPoint);
  // 最短定点を特定する
  if (onRoutePointIndexes.length === 1) {
    // [INFO] 定点を1点に特定できた場合はその定点を使用
    return onRoutePointIndexes[0];
  } else if (onRoutePointIndexes.length === 0) {
    // [INFO] 定点を0個の場合は一番近い定点からの距離を測る
    let shortestDistanceFromCenterPoint = Infinity; // 暫定最短距離を保持
    return routePoints.reduce((acc, _val, index) => {
      const geoPoint = turf.point([
        routePoints[index][1],
        routePoints[index][0],
      ]);

      // 定点と船舶位置の距離を求める
      const distanceFromCenterPoint = turf.distance(geoPoint, vesselGeoPoint, {
        units: "nauticalmiles",
      });
      // 暫定最短距離が更新されたらインデックスも更新する
      if (shortestDistanceFromCenterPoint > distanceFromCenterPoint) {
        shortestDistanceFromCenterPoint = Math.min(
          shortestDistanceFromCenterPoint,
          distanceFromCenterPoint,
        );
        acc = index;
      }
      return acc;
    }, 0);
  } else {
    // [INFO] 定点の候補が複数ある場合
    //        一番目的地に近い航路を使用する
    return Math.max(...onRoutePointIndexes);
  }
}

/**
 * 緯度経度をメルカトル座標に変換する
 * @param {*} point 座標（緯度経度）
 * @returns 座標（メルカトル座標）
 */
function getMercatorAtlasPosition(point) {
  const location = new Location(...point);
  const mercatorPoint = location.toMercatorAtlasPosition();
  return mercatorPoint;
}

/**
 * 残り航路の距離を取得
 * @param {*} routePoints 各航路の端点
 * @param {*} onRoutePointIndex 今いる航路のインデックス
 * @returns 残り航路区間の合計距離
 */
function getDistance(routePoints, onRoutePointIndex) {
  // 航路のマップ配列
  const seaRoutes = routePoints.reduce((acc, _val, index) => {
    if (index === 0) return [];
    const startPoint = turf.point([
      routePoints[index - 1][1],
      routePoints[index - 1][0],
    ]);
    const endPoint = turf.point([routePoints[index][1], routePoints[index][0]]);
    acc.push({
      routeIndex: index,
      start: routePoints[index - 1],
      end: routePoints[index],
      distance: turf.distance(startPoint, endPoint, {
        units: "nauticalmiles",
      }),
    });
    return acc;
  }, []);

  return seaRoutes.reduce((acc, val, index) => {
    // 今いる航路までは計算に含めない
    if (index < onRoutePointIndex) return 0;
    return (acc += val.distance);
  }, 0);
}
